// Admin → Mentor pool. PRD §7.7. Phase 2.1 minimal: list, view detail,
// activate (mints + shows setup link to copy/paste), decline.
import { useEffect, useState } from "react";
import { Loader2, Check, X, Copy, Plus, Mail, Send, Trash2, ExternalLink } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";

const BASE = import.meta.env.BASE_URL;

function authHeaders() {
  const token = localStorage.getItem("admin_token") || "";
  return { "Content-Type": "application/json", "x-admin-token": token };
}

interface Mentor {
  id: number; email: string; fullName: string; headline: string | null;
  status: string; expertiseTags: string[];
  acquisitionSource: string; pcConfidenceRating: number | null;
  firstLoggedInAt: string | null; activationInvitedAt: string | null;
  activeEngagements: number; completedEngagements: number;
}

export default function AdminMentors() {
  const [rows, setRows] = useState<Mentor[] | null>(null);
  const [filter, setFilter] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [showSetup, setShowSetup] = useState<{ url: string; mentorName: string } | null>(null);
  const [showInviteSent, setShowInviteSent] = useState<{ mentorName: string; email: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [busyAction, setBusyAction] = useState<"activate" | "send" | "decline" | "delete" | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createForm, setCreateForm] = useState({ fullName: "", email: "", headline: "", bio: "", linkedinUrl: "", expertiseTags: "" });

  function refresh() {
    const q = filter ? `?status=${filter}` : "";
    fetch(`${BASE}api/admin/mentors${q}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `${r.status}`)))
      .then(d => setRows(d.mentors)).catch(e => setErr(String(e)));
  }
  useEffect(refresh, [filter]);

  async function activate(m: Mentor) {
    if (busyId) return;
    setBusyId(m.id); setBusyAction("activate");
    try {
      const r = await fetch(`${BASE}api/admin/mentors/${m.id}/activate`, { method: "POST", headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) { alert(d.error || "Failed"); return; }
      setShowSetup({ url: d.setupUrl, mentorName: m.fullName });
      refresh();
    } finally { setBusyId(null); setBusyAction(null); }
  }
  // Send (or re-send) the onboarding email via Resend. The button sits next
  // to "Activate" so PC can either copy a link manually OR have the platform
  // email the mentor — same token primitive, two delivery paths.
  async function sendInvite(m: Mentor) {
    if (busyId) return;
    setBusyId(m.id); setBusyAction("send");
    try {
      const r = await fetch(`${BASE}api/admin/mentors/${m.id}/send-invite`, { method: "POST", headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) {
        // 502 means the email failed but the token was minted — fall back
        // to showing the setup URL so PC can still get the mentor in.
        if (d.setupUrl) { setShowSetup({ url: d.setupUrl, mentorName: m.fullName }); }
        alert(d.error || "Failed to send invite");
        return;
      }
      setShowInviteSent({ mentorName: m.fullName, email: d.sentTo || m.email });
      refresh();
    } finally { setBusyId(null); setBusyAction(null); }
  }
  // Hard-delete escape hatch. Used to clean up stray test rows /
  // duplicates created before activation. Server refuses if mentor has
  // engagements or interests on record — for those, decline/pause instead.
  async function deleteMentor(m: Mentor) {
    if (busyId) return;
    if (!window.confirm(`Permanently delete mentor "${m.fullName}" (${m.email})?\n\nThis cannot be undone. Use Decline instead if you want to keep the record for audit.`)) return;
    setBusyId(m.id); setBusyAction("delete");
    try {
      const r = await fetch(`${BASE}api/admin/mentors/${m.id}`, { method: "DELETE", headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { alert(d.error || "Failed to delete"); return; }
      refresh();
    } finally { setBusyId(null); setBusyAction(null); }
  }
  async function decline(m: Mentor) {
    const reason = window.prompt("Decline reason (optional)?") ?? "";
    if (busyId) return;
    setBusyId(m.id); setBusyAction("decline");
    try {
      const r = await fetch(`${BASE}api/admin/mentors/${m.id}/decline`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ reason }),
      });
      if (!r.ok) { alert("Failed"); return; }
      refresh();
    } finally { setBusyId(null); setBusyAction(null); }
  }
  async function createMentor(e: React.FormEvent) {
    e.preventDefault();
    if (createBusy) return;
    setCreateBusy(true);
    try {
      const tags = createForm.expertiseTags.split(",").map(t => t.trim()).filter(Boolean);
      const r = await fetch(`${BASE}api/admin/mentors`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          fullName: createForm.fullName.trim(),
          email: createForm.email.trim().toLowerCase(),
          headline: createForm.headline.trim() || null,
          bio: createForm.bio.trim() || null,
          linkedinUrl: createForm.linkedinUrl.trim() || null,
          expertiseTags: tags,
        }),
      });
      const d = await r.json();
      if (!r.ok) { alert(d.error || "Failed to create mentor"); return; }
      setCreateOpen(false);
      setCreateForm({ fullName: "", email: "", headline: "", bio: "", linkedinUrl: "", expertiseTags: "" });
      refresh();
    } finally { setCreateBusy(false); }
  }

  if (err) return <div className="max-w-5xl mx-auto px-6 py-10 text-sm text-red-600">{err}</div>;
  if (!rows) return <div className="max-w-5xl mx-auto px-6 py-10"><ProgressLoader active={true} title="Loading" stages={["Loading mentor pool", "Counting engagements"]} /></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Mentors</h1>
          <p className="text-sm text-muted-foreground">Pool, vetting, and activation across all cohorts.</p>
          <div className="mt-2 inline-flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Mentor login URL:</span>
            <a
              href={`${BASE}mentor/login`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-[#FF5C00] hover:underline"
              data-testid="link-mentor-login"
            >
              {`${window.location.origin}${BASE}mentor/login`}
              <ExternalLink className="w-3 h-3" />
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}${BASE}mentor/login`);
              }}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground border border-border rounded px-1.5 py-0.5"
              title="Copy mentor login URL"
              data-testid="button-copy-mentor-login"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        </div>
        <button onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white text-sm font-semibold rounded-md px-3 py-2"
          data-testid="button-add-mentor">
          <Plus className="w-4 h-4" /> Add mentor
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {["", "application", "vetting", "pending_activation", "active", "paused", "closed"].map(f => (
          <button key={f || "all"} onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded ${filter === f ? "bg-[#FF5C00] text-white" : "bg-muted hover:bg-muted/70"}`}
            data-testid={`filter-${f || "all"}`}>
            {f || "all"}
          </button>
        ))}
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Tags</th>
              <th className="text-right px-3 py-2 font-medium">Active / Done</th>
              <th className="text-right px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No mentors match this filter.</td></tr>
            ) : rows.map(m => (
              <tr key={m.id} className="border-t border-border" data-testid={`mentor-row-${m.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{m.fullName}</div>
                  <div className="text-xs text-muted-foreground">{m.email}</div>
                  {m.headline && <div className="text-xs text-muted-foreground italic mt-0.5">{m.headline}</div>}
                </td>
                <td className="px-3 py-2"><span className="text-[10px] font-medium px-2 py-0.5 rounded bg-muted">{m.status}</span></td>
                <td className="px-3 py-2 text-xs">{m.expertiseTags?.slice(0, 4).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-right text-xs">{m.activeEngagements} / {m.completedEngagements}</td>
                <td className="px-3 py-2 text-right">
                  {(m.status === "vetting" || m.status === "application") && (
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => activate(m)} disabled={busyId === m.id}
                        title="Mint a setup link to copy + paste manually"
                        className="inline-flex items-center gap-1 bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 text-white text-xs font-semibold rounded px-2.5 py-1"
                        data-testid={`button-activate-${m.id}`}>
                        {busyId === m.id && busyAction === "activate" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Activate
                      </button>
                      <button onClick={() => sendInvite(m)} disabled={busyId === m.id}
                        title="Mint a token AND email the mentor a Favcy Select onboarding email"
                        className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-600/90 disabled:opacity-40 text-white text-xs font-semibold rounded px-2.5 py-1"
                        data-testid={`button-send-invite-${m.id}`}>
                        {busyId === m.id && busyAction === "send" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send onboarding email
                      </button>
                      <button onClick={() => decline(m)} disabled={busyId === m.id}
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded px-2.5 py-1"
                        data-testid={`button-decline-${m.id}`}>
                        <X className="w-3 h-3" /> Decline
                      </button>
                    </div>
                  )}
                  {m.status === "pending_activation" && (
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => sendInvite(m)} disabled={busyId === m.id}
                        title="Re-send the onboarding email with a fresh setup link"
                        className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-600/90 disabled:opacity-40 text-white text-xs font-semibold rounded px-2.5 py-1"
                        data-testid={`button-resend-invite-${m.id}`}>
                        {busyId === m.id && busyAction === "send" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Re-send onboarding email
                      </button>
                      <button onClick={() => deleteMentor(m)} disabled={busyId === m.id}
                        title="Permanently delete this mentor row"
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 border border-red-500/40 hover:border-red-500/70 rounded px-2.5 py-1"
                        data-testid={`button-delete-${m.id}`}>
                        {busyId === m.id && busyAction === "delete" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Delete
                      </button>
                    </div>
                  )}
                  {m.status === "active" && (
                    <button onClick={() => deleteMentor(m)} disabled={busyId === m.id}
                      title="Permanently delete this mentor row (refused if any engagements/interests exist)"
                      className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 border border-red-500/40 hover:border-red-500/70 rounded px-2.5 py-1"
                      data-testid={`button-delete-${m.id}`}>
                      {busyId === m.id && busyAction === "delete" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold">Add mentor</h3>
              <button onClick={() => setCreateOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={createMentor} className="space-y-3">
              <div>
                <label className="text-xs font-medium block mb-1">Full name *</label>
                <input value={createForm.fullName} onChange={e => setCreateForm({ ...createForm, fullName: e.target.value })} required
                  className="w-full bg-muted/50 border border-border rounded px-3 py-2 text-sm" data-testid="input-create-fullname" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Email *</label>
                <input type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} required
                  className="w-full bg-muted/50 border border-border rounded px-3 py-2 text-sm" data-testid="input-create-email" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Headline</label>
                <input value={createForm.headline} onChange={e => setCreateForm({ ...createForm, headline: e.target.value })}
                  placeholder="e.g. Ex-VP Product, Razorpay"
                  className="w-full bg-muted/50 border border-border rounded px-3 py-2 text-sm" data-testid="input-create-headline" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Bio</label>
                <textarea value={createForm.bio} onChange={e => setCreateForm({ ...createForm, bio: e.target.value })} rows={3}
                  className="w-full bg-muted/50 border border-border rounded px-3 py-2 text-sm resize-none" data-testid="input-create-bio" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">LinkedIn URL</label>
                <input value={createForm.linkedinUrl} onChange={e => setCreateForm({ ...createForm, linkedinUrl: e.target.value })}
                  className="w-full bg-muted/50 border border-border rounded px-3 py-2 text-sm" data-testid="input-create-linkedin" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Expertise tags (comma-separated)</label>
                <input value={createForm.expertiseTags} onChange={e => setCreateForm({ ...createForm, expertiseTags: e.target.value })}
                  placeholder="fintech, growth, product-led-growth"
                  className="w-full bg-muted/50 border border-border rounded px-3 py-2 text-sm" data-testid="input-create-tags" />
              </div>
              <p className="text-xs text-muted-foreground">Mentor lands in <code className="bg-muted px-1 rounded">vetting</code>. Click <em>Activate</em> in the table to mint a setup link.</p>
              <button type="submit" disabled={createBusy || !createForm.fullName.trim() || !createForm.email.trim()}
                className="w-full bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 text-white text-sm font-semibold rounded-md py-2 flex items-center justify-center gap-2"
                data-testid="button-create-mentor-submit">
                {createBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {createBusy ? "Creating…" : "Add mentor"}
              </button>
            </form>
          </div>
        </div>
      )}

      {showInviteSent && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <Send className="w-4 h-4 text-emerald-600" />
              </div>
              <h3 className="text-base font-bold">Onboarding email sent</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              We emailed <strong className="text-foreground">{showInviteSent.mentorName}</strong> at <code className="text-xs bg-muted px-1 py-0.5 rounded">{showInviteSent.email}</code> with a Favcy Select set-password link.
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              The link expires in 24 hours. If they don't see it, ask them to check spam, then re-send from this row.
            </p>
            <button onClick={() => setShowInviteSent(null)} className="w-full bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white text-sm font-semibold rounded-md py-2">
              Done
            </button>
          </div>
        </div>
      )}

      {showSetup && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-base font-bold mb-2">Setup link for {showSetup.mentorName}</h3>
            <p className="text-sm text-muted-foreground mb-3">Copy this link and email it to the mentor manually. The link is valid for 24 hours and is single-use.</p>
            <div className="flex items-center gap-2">
              <input readOnly value={showSetup.url} className="flex-1 bg-muted/50 border border-border rounded px-2 py-1.5 text-xs font-mono" data-testid="input-setup-url" />
              <button onClick={() => navigator.clipboard.writeText(showSetup.url)}
                className="bg-[#FF5C00] text-white text-xs font-semibold rounded px-2 py-1.5 inline-flex items-center gap-1">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <button onClick={() => setShowSetup(null)} className="mt-4 text-sm text-muted-foreground hover:text-foreground">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
